import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiSearch, FiFilter, FiEye, FiEdit, FiTrash2, FiDownload } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { formatRandSimple, formatRandNoDecimals } from '../../utils/currency';
import { downloadInvoicePDF } from '../../utils/pdfGenerator';
import styles from './InvoiceList.module.css';

interface Invoice {
  id: string;
  invoiceNumber: string;
  clientName: string;
  amount: number;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  issueDate: string;
  items: InvoiceItem[];
}

interface InvoiceItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

// Mock data
const mockInvoices: Invoice[] = [
  {
    id: '1',
    invoiceNumber: 'INV-2024-001',
    clientName: 'Acme Corporation',
    amount: 2500,
    status: 'paid',
    dueDate: '2024-01-15',
    issueDate: '2024-01-01',
    items: [
      { id: '1', description: 'Web Development Services', quantity: 40, unitPrice: 50, total: 2000 },
      { id: '2', description: 'UI/UX Design', quantity: 10, unitPrice: 50, total: 500 }
    ]
  },
  {
    id: '2',
    invoiceNumber: 'INV-2024-002',
    clientName: 'Tech Solutions Inc',
    amount: 1800,
    status: 'sent',
    dueDate: '2024-01-20',
    issueDate: '2024-01-05',
    items: [
      { id: '1', description: 'Consulting Services', quantity: 20, unitPrice: 90, total: 1800 }
    ]
  },
  {
    id: '3',
    invoiceNumber: 'INV-2024-003',
    clientName: 'Global Industries',
    amount: 3200,
    status: 'overdue',
    dueDate: '2024-01-10',
    issueDate: '2023-12-20',
    items: [
      { id: '1', description: 'Software Development', quantity: 80, unitPrice: 40, total: 3200 }
    ]
  },
  {
    id: '4',
    invoiceNumber: 'INV-2024-004',
    clientName: 'Innovation Labs',
    amount: 1500,
    status: 'draft',
    dueDate: '2024-02-01',
    issueDate: '2024-01-10',
    items: [
      { id: '1', description: 'Project Management', quantity: 15, unitPrice: 100, total: 1500 }
    ]
  }
];

export function InvoiceList() {
  const [invoices, setInvoices] = useState<Invoice[]>(mockInvoices);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         invoice.clientName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      setInvoices(invoices.filter(invoice => invoice.id !== id));
    }
  };

  const handleBulkDelete = () => {
    if (selectedInvoices.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedInvoices.length} invoice(s)?`)) {
      setInvoices(invoices.filter(invoice => !selectedInvoices.includes(invoice.id)));
      setSelectedInvoices([]);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedInvoices(filteredInvoices.map(invoice => invoice.id));
    } else {
      setSelectedInvoices([]);
    }
  };

  const handleSelectInvoice = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedInvoices([...selectedInvoices, id]);
    } else {
      setSelectedInvoices(selectedInvoices.filter(invoiceId => invoiceId !== id));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses = {
      draft: styles.statusDraft,
      sent: styles.statusSent,
      paid: styles.statusPaid,
      overdue: styles.statusOverdue,
      cancelled: styles.statusCancelled
    };
    return `${styles.statusBadge} ${statusClasses[status as keyof typeof statusClasses]}`;
  };

  const getStatusText = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleDownloadPDF = async (invoice: Invoice) => {
    try {
      // Prepare data for PDF generation
      const pdfData = {
        invoiceNumber: invoice.invoiceNumber,
        issueDate: invoice.issueDate,
        dueDate: invoice.dueDate,
        client: {
          name: invoice.clientName,
          email: '', // Not available in this mock data
          phone: '', // Not available in this mock data
          company: invoice.clientName,
          address: undefined
        },
        items: invoice.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.total
        })),
        subtotal: invoice.amount,
        tax: 0, // Not available in this mock data
        total: invoice.amount,
        notes: '',
        terms: ''
      };

      await downloadInvoicePDF(pdfData);
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Invoices</h1>
          <p className={styles.subtitle}>Manage your invoices and track payments</p>
        </div>
        <div className={styles.headerActions}>
          <Link to="/invoices/create-from-quote" className={styles.createFromQuoteButton}>
            <FiPlus size={16} />
            Create from Quote
          </Link>
          <Link to="/invoices/create" className={styles.createButton}>
            <FiPlus size={16} />
            New Invoice
          </Link>
        </div>
      </div>

      {/* Filters and Search */}
      <div className={styles.filters}>
        <div className={styles.searchBox}>
          <FiSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <div className={styles.filterGroup}>
          <FiFilter className={styles.filterIcon} />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.statusFilter}
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedInvoices.length > 0 && (
        <div className={styles.bulkActions}>
          <span>{selectedInvoices.length} invoice(s) selected</span>
          <button onClick={handleBulkDelete} className={styles.bulkDeleteButton}>
            <FiTrash2 size={16} />
            Delete Selected
          </button>
        </div>
      )}

      {/* Invoices Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={selectedInvoices.length === filteredInvoices.length && filteredInvoices.length > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className={styles.checkbox}
                />
              </th>
              <th>Invoice #</th>
              <th>Client</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Issue Date</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => (
              <tr key={invoice.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedInvoices.includes(invoice.id)}
                    onChange={(e) => handleSelectInvoice(invoice.id, e.target.checked)}
                    className={styles.checkbox}
                  />
                </td>
                <td className={styles.invoiceNumber}>{invoice.invoiceNumber}</td>
                <td className={styles.clientName}>{invoice.clientName}</td>
                <td className={styles.amount}>{formatRandNoDecimals(invoice.amount)}</td>
                <td>
                  <span className={getStatusBadge(invoice.status)}>
                    {getStatusText(invoice.status)}
                  </span>
                </td>
                <td>{new Date(invoice.issueDate).toLocaleDateString()}</td>
                <td>{new Date(invoice.dueDate).toLocaleDateString()}</td>
                <td className={styles.actions}>
                  <Link to={`/invoices/${invoice.id}`} className={styles.actionButton} title="View">
                    <FiEye size={16} />
                  </Link>
                  <Link to={`/invoices/${invoice.id}/edit`} className={styles.actionButton} title="Edit">
                    <FiEdit size={16} />
                  </Link>
                  <button 
                    onClick={() => handleDownloadPDF(invoice)} 
                    className={styles.actionButton} 
                    title="Download PDF"
                  >
                    <FiDownload size={16} />
                  </button>
                  <button 
                    onClick={() => handleDelete(invoice.id)} 
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredInvoices.length === 0 && (
        <div className={styles.emptyState}>
          <p>No invoices found. Create your first invoice to get started.</p>
          <Link to="/invoices/create" className={styles.createButton}>
            <FiPlus size={16} />
            Create Invoice
          </Link>
        </div>
      )}
    </div>
  );
}
